/**
 * Help Center Navigation Component
 * Provides structured navigation for help articles with category organization
 */

import { BlockStack, InlineStack, Text, Icon, Box, Divider } from '@shopify/polaris';
import { Link, useParams } from 'react-router';
import {
  QuestionCircleIcon,
  SettingsIcon,
  BookIcon,
  AlertCircleIcon,
} from '@shopify/polaris-icons';
import type { HelpArticle } from '../../utils/help-center';

interface HelpNavigationProps {
  articles: Omit<HelpArticle, 'content'>[];
  queryString?: string;
}

const categoryConfig = {
  'getting-started': {
    icon: QuestionCircleIcon,
    label: 'Getting Started',
    description: 'New to Stock Sentry? Start here.',
  },
  configuration: {
    icon: SettingsIcon,
    label: 'Configuration',
    description: 'Customize alerts and notifications.',
  },
  features: {
    icon: BookIcon,
    label: 'Features',
    description: 'Learn what Stock Sentry can do.',
  },
  troubleshooting: {
    icon: AlertCircleIcon,
    label: 'Troubleshooting',
    description: 'Resolve common issues quickly.',
  },
} as const;

export function HelpNavigation({ articles, queryString = '' }: HelpNavigationProps) {
  const params = useParams();
  const currentSlug = params.slug;

  // Group articles by category
  const articlesByCategory = articles.reduce(
    (acc, article) => {
      if (!acc[article.category]) {
        acc[article.category] = [];
      }
      acc[article.category].push(article);
      return acc;
    },
    {} as Record<string, typeof articles>
  );

  return (
    <BlockStack gap="400">
      {(Object.keys(categoryConfig) as Array<keyof typeof categoryConfig>).map((categoryKey) => {
        const category = categoryConfig[categoryKey];
        const categoryArticles = articlesByCategory[categoryKey] || [];

        if (categoryArticles.length === 0) return null;

        return (
          <Box key={categoryKey}>
            <BlockStack gap="200">
              {/* Category Header */}
              <InlineStack gap="200" align="start">
                <Icon source={category.icon} tone="base" />
                <BlockStack gap="050">
                  <Text as="h3" variant="headingSm" fontWeight="semibold">
                    {category.label}
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    {category.description}
                  </Text>
                </BlockStack>
              </InlineStack>

              {/* Article Links */}
              <BlockStack gap="100">
                {categoryArticles.map((article) => {
                  const isActive = currentSlug === article.slug;

                  return (
                    <Link
                      key={article.slug}
                      to={`/app/help/${article.slug}${queryString ? `?${queryString}` : ''}`}
                      style={{ textDecoration: 'none' }}
                    >
                      <Box
                        padding="200"
                        borderRadius="100"
                        background={isActive ? 'bg-fill-selected' : undefined}
                      >
                        <InlineStack gap="100" blockAlign="center" wrap={false}>
                          <Box minWidth="4px">
                            {isActive && (
                              <Box
                                minWidth="4px"
                                minHeight="16px"
                                background="bg-fill-active"
                                borderRadius="100"
                              />
                            )}
                          </Box>
                          <BlockStack gap="050">
                            <Text
                              as="span"
                              variant="bodySm"
                              fontWeight={isActive ? 'semibold' : 'regular'}
                              tone={isActive ? 'base' : 'subdued'}
                            >
                              {article.title}
                            </Text>
                          </BlockStack>
                        </InlineStack>
                      </Box>
                    </Link>
                  );
                })}
              </BlockStack>
            </BlockStack>

            <Box paddingBlockStart="300">
              <Divider />
            </Box>
          </Box>
        );
      })}

      {/* Help Resources Footer */}
      <Box>
        <BlockStack gap="200">
          <Text as="h3" variant="headingSm" fontWeight="semibold">
            Need More Help?
          </Text>
          <BlockStack gap="100">
            <Text as="p" variant="bodySm" tone="subdued">
              Can't find what you're looking for?
            </Text>
            <Link
              to={`/app/support${queryString ? `?${queryString}` : ''}`}
              style={{ textDecoration: 'none', color: '#008060' }}
            >
              <Text as="span" variant="bodySm" fontWeight="semibold">
                Contact Support →
              </Text>
            </Link>
          </BlockStack>
        </BlockStack>
      </Box>
    </BlockStack>
  );
}
